import {ApiResponseDto, BasePopupProps, DropDownListItem, NameWithId} from "@utils/Models";
import FlintLoadingWrapper from "@components/page_components/common/loading/FlintLoadingWrapper";
import { useEffect, useMemo, useState} from "react";
import FlintButton from "@components/controls/FlintButton";
import {WindowActionsBar} from "@progress/kendo-react-dialogs";
import {useTranslation} from "react-i18next";
import FlintTextBox from "@components/controls/FlintTextBox";
import FlintDropDownList from "@components/controls/FlintDropDownList";
import {useAuthenticationContext} from "@utils/auth/AuthenticationContext";
import {
    NewTrackingNumberRequest,
    OrderPreviewBasicDataModel,
    OrderPreviewModelStateType
} from "../../order.models";
import FlintDateTimePicker from "@components/controls/FlintDateTimePicker";
import axios from "axios";
import {ApiUrl_Orders_AddTrackingNumber} from "@utils/Endpoints";
import {
    useFlintNotificationContext
} from "@components/page_components/common/notifications/FlintNotificationContext";

export default function AddDeliveryDetailsComponent(props: AddDeliveryDetailsComponentProps) {
    const {state, handlers, utils} = useAddDeliveryDetailsComponent(props);
    const {t} = useTranslation();
    return (
        <FlintLoadingWrapper showLoading={state.isLoadingState.get} loadingWrapperClassName={"d-grid h-100"}>
            <div className={"row"}>
                <div className={"col-6 form-group"}>
                    <FlintDateTimePicker label={`1. ${t("ORDER_PREVIEW_TRACKINGNUMBER_NEW_AddDateField")}`}
                                         onChange={x => handlers.handleUpdateTrackingNumberModel("DispatchDate", x.value)}
                                         value={state.trackingNumberModelState.get.DispatchDate}/>
                </div>
                <div className={"col-6 form-group"}>
                    <FlintTextBox label={`2. ${t("ORDER_PREVIEW_TRACKINGNUMBER_NEW_AddNumberField")}`}
                                  onChange={x => handlers.handleUpdateTrackingNumberModel("TrackingNumber", x.value)}
                                  disabled={state.disabledStateModelState.get.TrackingNumberDisabled}
                                  value={state.trackingNumberModelState.get.TrackingNumber}/>
                </div>
            </div>
            <div className={"row gx-0 align-items-center"}>
                <div className={"col-5 form-group"}>
                    <FlintDropDownList<DropDownListItem>
                        selectedValue={state.trackingNumberModelState.get.TrackingTemplateId}
                        defaultItem={{Id: 0, Name: ''}}
                        onChange={x => handlers.handleUpdateTrackingNumberModel("TrackingTemplateId", x.value?.Id)}
                        disabled={state.disabledStateModelState.get.TrackingTemplateIdDisabled}
                        usePrimitives={true}
                        label={`3. ${t("ORDER_PREVIEW_TRACKINGNUMBER_NEW_SelectTemplateField")}`}
                        dataSource={utils.templateList}/>
                </div>
                <div className={"col-1 form-group text-center"}>{t("COMMON_Or")}</div>
                <div className={"col-6 form-group"}>
                    <FlintTextBox label={`3. ${t("ORDER_PREVIEW_TRACKINGNUMBER_NEW_EnterCustomTrackingField")}`}
                                  onChange={x => handlers.handleUpdateTrackingNumberModel("TrackingUrl", x.value)}
                                  disabled={state.disabledStateModelState.get.TrackingUrlDisabled}
                                  value={state.trackingNumberModelState.get.TrackingUrl}
                                  type={"url"}/>
                </div>
            </div>
            <div className={"row"}>
                <div className={"col-12 form-group"}>
                    <FlintTextBox label={`4. ${t("ORDER_PREVIEW_TRACKINGNUMBER_NEW_AddDisplayedTextField")}`}
                                  onChange={x => handlers.handleUpdateTrackingNumberModel("TrackingNumberDisplay", x.value)}
                                  disabled={state.disabledStateModelState.get.TrackingNumberDisplayDisabled}
                                  value={state.trackingNumberModelState.get.TrackingNumberDisplay}/>
                </div>
            </div>
            <WindowActionsBar layout={"center"}>
                <FlintButton showLoadingIcon={state.isLoadingState.get} disabled={state.isLoadingState.get}
                             themeColor={"secondary"}
                             onClick={handlers.handleCancelled}>{t("Cancel")}</FlintButton>
                <FlintButton showLoadingIcon={state.isLoadingState.get}
                             disabled={state.isLoadingState.get || state.disabledStateModelState.get.TrackingNumberDisplayDisabled}
                             themeColor={"primary"}
                             onClick={handlers.handleConfirmed}>{t("Confirm")}</FlintButton>
            </WindowActionsBar>
        </FlintLoadingWrapper>
    )
}

export interface AddDeliveryDetailsComponentProps extends BasePopupProps {
    orderPreviewModelState: OrderPreviewModelStateType
}

function useAddDeliveryDetailsComponent(props: AddDeliveryDetailsComponentProps) {
    //const {state} = useOrderPreviewContext();
    const [isLoading, setIsLoading] = useState(false);
    const {rootModel} = useAuthenticationContext();
    const {showNotification} = useFlintNotificationContext();
    const [trackingNumberModel, setTrackingNumberModel] = useState<{
        DispatchDate?: Date;
        TrackingNumber?: string;
        TrackingTemplateId?: number;
        TrackingUrl?: string;
        TrackingNumberDisplay?: string;
    }>({DispatchDate: new Date()});

    const [disabledStateModel, setDisabledStateModel] = useState<{
        TrackingNumberDisabled: boolean;
        TrackingTemplateIdDisabled: boolean;
        TrackingUrlDisabled: boolean;
        TrackingNumberDisplayDisabled: boolean;
    }>({
        TrackingNumberDisabled: false,
        TrackingNumberDisplayDisabled: true,
        TrackingTemplateIdDisabled: true,
        TrackingUrlDisabled: true,
    });

    function handleUpdateTrackingNumberModel<T extends keyof {
        DispatchDate?: Date;
        TrackingNumber?: string;
        TrackingTemplateId?: number;
        TrackingUrl?: string;
        TrackingNumberDisplay?: string
    }>(key: T, value: any) {
        setTrackingNumberModel(prev => ({
            ...prev,
            [key]: value
        }))
    }

    // function updateDisabledStateModel<T extends keyof {
    //     TrackingNumberDisabled: boolean;
    //     TrackingTemplateIdDisabled: boolean;
    //     TrackingUrlDisabled: boolean;
    //     TrackingNumberDisplayDisabled: boolean
    // }>(key: T, value: any) {
    //     setDisabledStateModel(prev => ({
    //         ...prev,
    //         [key]: value
    //     }))
    // }

    useEffect(() => {
        const trackingNumberDisabled = !trackingNumberModel.DispatchDate;
        const trackingTemplateIdDisabled = !trackingNumberModel.TrackingNumber || (trackingNumberModel.TrackingUrl?.length ?? 0) > 0;
        const trackingUrlDisabled = !trackingNumberModel.TrackingNumber || (trackingNumberModel.TrackingTemplateId ?? 0) > 0;
        const trackingNumberDisplayDisabled = !trackingNumberModel.TrackingUrl && !trackingNumberModel.TrackingTemplateId;
        setDisabledStateModel({
            TrackingNumberDisabled: trackingNumberDisabled,
            TrackingTemplateIdDisabled: trackingTemplateIdDisabled,
            TrackingUrlDisabled: trackingUrlDisabled,
            TrackingNumberDisplayDisabled: trackingNumberDisplayDisabled,
        })
    }, [trackingNumberModel])

    function handleCancelled() {
        props.OnCancelled();
    }

    const templateList: NameWithId[] = useMemo(() => {
        return rootModel?.LabData.CurrentLab.Parameters.DeliveryUrlPatterns.PatternList.map(t => {
            return {
                Id: t.Id,
                Name: t.Name,
                IdString: t.UrlPattern,
                Deleted: false
            } as NameWithId
        }) ?? []
    }, [rootModel]);

    function handleConfirmed() {
        const post: NewTrackingNumberRequest = {
            OrderGuid: props.orderPreviewModelState.get.OrderBasicData.Guid,
            TrackingNumber: trackingNumberModel.TrackingNumber!,
            TrackingNumberDisplay: trackingNumberModel.TrackingNumberDisplay,
            TrackingTemplateId: trackingNumberModel.TrackingTemplateId,
            TrackingUrl: trackingNumberModel.TrackingUrl,
            DispatchDate: trackingNumberModel.DispatchDate!,
        };
        setIsLoading(true);
        axios.post<ApiResponseDto<OrderPreviewBasicDataModel>>(ApiUrl_Orders_AddTrackingNumber, post)
            .then(response => {
                props.orderPreviewModelState.set("OrderBasicData", response.data.Result);
                props.OnConfirmedAndSaved();
            })
            .catch(error => showNotification(error))
            .finally(() => setIsLoading(false));
    }

    return {
        state: {
            isLoadingState: {get: isLoading},
            trackingNumberModelState: {get: trackingNumberModel},
            disabledStateModelState: {get: disabledStateModel},
        },
        handlers: {handleConfirmed, handleCancelled, handleUpdateTrackingNumberModel},
        utils: {
            templateList,
        }
    }
}